import { SelectList, SelectListEvent } from '@acies/core';
import { IPositions } from '../../../domain/positions';
import { ConfiguratorModel } from '../configurator.model';
import PositionItemRenderer from './position-item-renderer/position-item-renderer';
import PositionsListEvent from './positions-list-event';

export default class PositionsList extends SelectList<IPositions> {
	public constructor() {
		super();
		// The arrow keys move the selection rather than a focus ring over it, so a position is
		// priced and drawn as the user walks the list. It also makes the list one tab stop, at the
		// selected row: tabbing through every position to reach the one that is selected would
		// leave the arrow keys with nothing to do. See SelectList.autoSelectWhenFocused.
		this.autoSelectWhenFocused = true;
		this.width = 'fill';
		this.height = 'fill';
		this.paddingHorizontal = 0;
		this.paddingVertical = 0;
		// the scrollbar is there from the first row rather than appearing at the one that overflows,
		// so the rows never shift under the header, which is aligned to it once
		this.alwaysShowVerticalScrollbar = true;
		this.data = ConfiguratorModel.positions;
		this.ItemRenderer = PositionItemRenderer;
		// the listener is registered on the model, so it would otherwise run in its scope rather
		// than in this one
		this.configuringChanged = this.configuringChanged.bind(this);
		// Subscribed here rather than on connect, for the reason DrawingSection gives. A row reads
		// what the model already holds whenever it is handed a position.
		ConfiguratorModel.positionsConfiguring.subscribe(this.configuringChanged);
	}

	/**
	 * Tells every row that the positions a configuration is out for have changed, so each says again whether its own is waiting on an answer — see `PositionItemRenderer.configuringChanged`.
	 *
	 * Handed on by the list rather than heard by each row, because the rows are pooled: one taken out and put back is never connected again in the sense a subscription made on connect needs, and one subscribed for good would be held by the model after the list had gone. The list is subscribed once, for all of them.
	 */
	public configuringChanged(): void {
		for (const itemRenderer of this.itemRenderers()) {
			// cast rather than checked: the list sets its ItemRenderer to PositionItemRenderer, and draws no other
			(itemRenderer as PositionItemRenderer).configuringChanged();
		}
	}

	/**
	 * Unsubscribes, because the model outlives the list.
	 *
	 * `ConfiguratorModel` is a module constant, so a listener it still holds is a list that has been removed and cannot be collected.
	 */
	protected override disconnected(): void {
		super.disconnected();
		ConfiguratorModel.positionsConfiguring.unsubscribe(this.configuringChanged);
	}

	protected override initialize(): void {
		super.initialize();
		this.#addEventListeners();
	}

	/**
	 * The SelectListEvent is caught here rather than left to bubble, so the list is the only place that speaks about list items.
	 *
	 * The listener needs no binding: `this` is the currentTarget, which is this list.
	 */
	#addEventListeners(): void {
		this.addEventListener(SelectListEvent.SELECTION_CHANGED, this.#selectionChanged as EventListener);
	}

	/**
	 * Answers the SelectList selection with a PositionsListEvent.
	 *
	 * The SelectList raises this both when a position is selected and when it is de-selected, and the selectedItem is null in the latter case, so the one path covers both.
	 */
	#selectionChanged(event: SelectListEvent<IPositions | null>): void {
		event.stopPropagation();
		this.dispatchEvent(new PositionsListEvent(event.selectedItem));
	}
}
customElements.define('cf-positions-list', PositionsList);
